import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkle, PaperPlaneRight, Camera, X, CheckCircle, Warning, ArrowLeft, Wrench,
} from "@phosphor-icons/react";
import Illustration from "@/components/TriageIllustrations";
import PestPicker from "@/components/PestPicker";

const BASE = process.env.REACT_APP_BACKEND_URL;

const URGENCY_STYLES = {
  normal:    { label: "Normal",     bg: "bg-cyan-100 text-cyan-900 border-cyan-500" },
  same_day:  { label: "Same Day",   bg: "bg-amber-100 text-amber-900 border-amber-500" },
  emergency: { label: "EMERGENCY",  bg: "bg-red-100 text-red-900 border-red-600" },
};

// Full-screen AI triage assistant that lives inside RequestRadarPortal.
// Two phases:
//   1) Chat with Claude to try and fix it or gather info
//   2) Escalate → creates a real WO via the existing submit endpoint
export default function TenantTriageChat({ token, info, onBack, onEscalated }) {
  const [phase, setPhase] = useState("picker"); // picker | pest_picker | chat | resolved | escalated
  const [quickIssues, setQuickIssues] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // {role, text, photos, quick_replies, photo_request}
  const [envelope, setEnvelope] = useState({}); // latest Claude reply meta
  const [input, setInput] = useState("");
  const [uploadingPhotos, setUploadingPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const [tenantPhone, setTenantPhone] = useState("");
  const [unit, setUnit] = useState("");
  const [entryOk, setEntryOk] = useState(false);
  const [openerText, setOpenerText] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [allowNoPhoto, setAllowNoPhoto] = useState(false);
  const scrollRef = useRef(null);
  const galleryRef = useRef(null);

  // Total photos attached across the whole session — from message history + pending uploads
  const totalPhotos =
    messages.reduce((n, m) => n + (m.photos?.length || 0), 0)
    + uploadingPhotos.filter(u => !u.pending && u.path).length;
  const isEmergencyState = envelope.urgency === "emergency";
  const canEscalate = totalPhotos >= 1 || allowNoPhoto || isEmergencyState;

  useEffect(() => {
    axios.get(`${BASE}/api/public/report/${token}/triage/quick-issues`)
      .then(r => setQuickIssues(r.data.quick_issues || []))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    // auto-scroll to bottom on new message
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 50);
  }, [messages]);

  const startSession = async (quick_key = "", text = "") => {
    if (!tenantName.trim()) { toast.error("Please enter your name first"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${BASE}/api/public/report/${token}/triage/start`, {
        quick_key, text, tenant_name: tenantName,
      });
      setSessionId(data.session_id);
      // Seed with the user's implicit opener + AI first reply
      const opener = quick_key
        ? (quickIssues.find(q => q.key === quick_key)?.label || "Maintenance issue")
        : (text || "Maintenance issue");
      setMessages([
        { role: "user", text: opener, photos: [] },
        {
          role: "assistant",
          text: data.reply.message,
          quick_replies: data.reply.quick_replies || [],
          photo_request: data.reply.photo_request || "",
          illustration_key: data.reply.illustration_key || "",
        },
      ]);
      setEnvelope(data.reply);
      setPhase("chat");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't start the AI helper"));
    } finally { setBusy(false); }
  };

  const uploadPhoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await axios.post(`${BASE}/api/public/report/${token}/photo`, fd,
      { headers: { "Content-Type": "multipart/form-data" }});
    return data.path;
  };

  const onPickPhotos = async (files) => {
    const arr = Array.from(files);
    setUploadingPhotos(prev => [...prev, ...arr.map(f => ({ name: f.name, pending: true }))]);
    for (const f of arr) {
      try {
        const path = await uploadPhoto(f);
        setUploadingPhotos(prev => {
          const next = [...prev];
          const idx = next.findIndex(x => x.name === f.name && x.pending);
          if (idx >= 0) next[idx] = { name: f.name, path, pending: false };
          return next;
        });
      } catch (e) {
        toast.error("Photo upload failed");
      }
    }
  };

  const sendMessage = async (textOverride = null) => {
    const text = (textOverride ?? input).trim();
    const photos = uploadingPhotos.filter(x => !x.pending && x.path).map(x => x.path);
    if (!text && photos.length === 0) return;
    if (uploadingPhotos.some(x => x.pending)) { toast.error("Wait for the photo to finish uploading"); return; }
    setInput("");
    setSending(true);
    // Optimistically render the tenant's message
    setMessages(prev => [...prev, { role: "user", text, photos }]);
    setUploadingPhotos([]);
    try {
      const { data } = await axios.post(`${BASE}/api/public/report/${token}/triage/message`, {
        session_id: sessionId, text, photos,
      });
      setMessages(prev => [...prev, {
        role: "assistant", text: data.reply.message,
        quick_replies: data.reply.quick_replies || [],
        photo_request: data.reply.photo_request || "",
        illustration_key: data.reply.illustration_key || "",
      }]);
      setEnvelope(data.reply);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI didn't respond — please try again"));
    } finally { setSending(false); }
  };

  const markResolved = async () => {
    if (!window.confirm("Nice — mark this as fixed? No maintenance visit will be scheduled.")) return;
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/public/report/${token}/triage/resolve`, { session_id: sessionId });
      setPhase("resolved");
    } catch (e) { toast.error("Couldn't mark resolved"); }
    finally { setBusy(false); }
  };

  const escalate = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${BASE}/api/public/report/${token}/triage/escalate`, {
        session_id: sessionId,
        tenant_name: tenantName,
        tenant_phone: tenantPhone,
        unit, entry_ok: entryOk,
        allow_no_photo: allowNoPhoto || isEmergencyState,
      });
      setConfirmation(data);
      setPhase("escalated");
      if (onEscalated) onEscalated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't create the work order"));
    } finally { setBusy(false); }
  };

  // ── PEST PICKER ────────────────────────────────────────────────────
  if (phase === "pest_picker") {
    return (
      <PestPicker
        disabled={busy || !tenantName.trim()}
        onBack={() => setPhase("picker")}
        onPick={(pestKey, pestName, seedText) => {
          // Fire the triage session with pest_issue quick_key AND the
          // pest-specific opener so Claude jumps straight to actionable
          // triage instead of re-asking "what did you see?".
          startSession("pest_issue", seedText);
        }}
      />
    );
  }

  // ── PICKER ─────────────────────────────────────────────────────────
  if (phase === "picker") {
    return (
      <div className="min-h-screen bg-slate-100" data-testid="triage-picker">
        <header className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white"
          style={info?.brand_color ? { background: info.brand_color } : {}}>
          <div className="max-w-lg mx-auto px-3 py-3 flex items-center gap-3">
            <button onClick={onBack} className="h-9 w-9 border border-white/30 flex items-center justify-center hover:bg-white/10"
              data-testid="triage-back">
              <ArrowLeft weight="bold" size={16}/>
            </button>
            <div className="flex-1">
              <div className="text-[10px] font-black uppercase tracking-widest opacity-80">AI Maintenance Helper</div>
              <div className="font-display font-black text-lg leading-tight">Let's Try Fixing It First</div>
            </div>
            <Sparkle weight="fill" size={22} className="opacity-90"/>
          </div>
        </header>

        <div className="max-w-lg mx-auto p-3 space-y-3">
          <div className="p-4 bg-white border-2 border-violet-600">
            <div className="text-sm text-slate-700 leading-relaxed">
              I'm Field Radar's maintenance AI — trained on rental issues. Tell me what's up and I'll try to help you fix it in minutes.
              If it needs a tech, I'll gather everything for them so they show up prepared. <b>Most tenants solve their issue in under 3 minutes.</b>
            </div>
          </div>

          <div className="p-3 bg-white border-2 border-[#0f172a]">
            <FormField label="Your Name">
              <input value={tenantName} onChange={e => setTenantName(e.target.value)} data-testid="triage-name"
                className="w-full h-11 px-3 border-2 border-[#0f172a] font-medium text-sm"/>
            </FormField>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <FormField label="Phone (optional)">
                <input value={tenantPhone} type="tel" onChange={e => setTenantPhone(e.target.value)} data-testid="triage-phone"
                  className="w-full h-11 px-3 border-2 border-[#0f172a] font-medium text-sm"/>
              </FormField>
              <FormField label="Unit (if any)">
                <input value={unit} onChange={e => setUnit(e.target.value)} data-testid="triage-unit"
                  className="w-full h-11 px-3 border-2 border-[#0f172a] font-medium text-sm"/>
              </FormField>
            </div>
          </div>

          <div className="p-3 bg-white border-2 border-[#0f172a]">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">What's the issue?</div>
            <div className="grid grid-cols-2 gap-2" data-testid="triage-quick-issues">
              {quickIssues.map(qi => (
                <button key={qi.key} onClick={() => {
                  if (qi.key === "pest_issue") { setPhase("pest_picker"); return; }
                  startSession(qi.key);
                }} disabled={busy || !tenantName.trim()}
                  data-testid={`triage-issue-${qi.key}`}
                  className="p-3 border-2 border-slate-300 hover:border-violet-600 hover:bg-violet-50 disabled:opacity-40 text-left transition-colors">
                  <div className="text-2xl">{qi.icon}</div>
                  <div className="text-xs font-black uppercase tracking-tight mt-0.5">{qi.label}</div>
                  {qi.key === "pest_issue" && (
                    <div className="text-[9px] font-black uppercase tracking-widest text-amber-700 mt-0.5">
                      Tap to identify →
                    </div>
                  )}
                </button>
              ))}
            </div>
            <div className="mt-3 text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Or describe it in your own words</div>
            <div className="flex gap-2">
              <input value={openerText} onChange={e => setOpenerText(e.target.value)}
                placeholder="e.g. My kitchen sink is leaking under the cabinet…" data-testid="triage-opener"
                className="flex-1 h-11 px-3 border-2 border-slate-300 focus:border-[#0f172a] focus:outline-none text-sm"/>
              <button onClick={() => startSession("", openerText)}
                disabled={busy || !tenantName.trim() || !openerText.trim()} data-testid="triage-opener-send"
                className="h-11 px-4 bg-violet-600 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest">
                Start
              </button>
            </div>
          </div>

          <div className="text-center">
            <button onClick={onBack} data-testid="triage-skip-to-form"
              className="text-xs text-slate-600 underline hover:text-[#0f172a]">
              Skip AI — just submit a regular request →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── RESOLVED ──────────────────────────────────────────────────────
  if (phase === "resolved") {
    return (
      <div className="min-h-screen bg-emerald-50 flex items-center justify-center p-4" data-testid="triage-resolved">
        <div className="max-w-md w-full bg-white border-2 border-emerald-600 p-8 text-center">
          <div className="h-16 w-16 mx-auto bg-emerald-600 text-white rounded-full flex items-center justify-center">
            <CheckCircle weight="fill" size={40}/>
          </div>
          <h1 className="mt-4 font-display font-black text-3xl uppercase">Fixed!</h1>
          <p className="mt-2 text-sm text-slate-700">
            Awesome — glad we got that sorted without a truck roll. If it comes back, tap the same link and I'll help again.
          </p>
          <button onClick={onBack} className="mt-5 h-11 px-6 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest">
            Close
          </button>
        </div>
      </div>
    );
  }

  // ── ESCALATED ─────────────────────────────────────────────────────
  if (phase === "escalated" && confirmation) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4" data-testid="triage-escalated">
        <div className="max-w-md w-full bg-white border-2 border-[#0f172a] p-8 text-center">
          <div className="h-16 w-16 mx-auto bg-emerald-600 text-white rounded-full flex items-center justify-center">
            <CheckCircle weight="fill" size={40}/>
          </div>
          <h1 className="mt-4 font-display font-black text-3xl uppercase">Sent to Maintenance</h1>
          <p className="mt-2 text-sm text-slate-700">
            The crew has everything the AI collected — they'll roll prepared. Save this confirmation code:
          </p>
          <div className="mt-4 p-3 border-2 border-[#0f172a] bg-slate-50">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Confirmation Ref</div>
            <div className="mt-1 font-display font-black text-2xl tracking-wider">{confirmation.confirmation_ref}</div>
          </div>
        </div>
      </div>
    );
  }

  // ── CHAT ──────────────────────────────────────────────────────────
  const urgencyStyle = URGENCY_STYLES[envelope.urgency || "normal"];
  const isEmergency = envelope.urgency === "emergency";
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col" data-testid="triage-chat">
      <header className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white flex-shrink-0"
        style={info?.brand_color ? { background: info.brand_color } : {}}>
        <div className="max-w-lg mx-auto px-3 py-3 flex items-center gap-3">
          <button onClick={onBack} className="h-9 w-9 border border-white/30 flex items-center justify-center hover:bg-white/10"
            data-testid="triage-chat-back">
            <ArrowLeft weight="bold" size={16}/>
          </button>
          <div className="flex-1">
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">AI Maintenance Helper</div>
            <div className="font-display font-black text-lg leading-tight">Field Radar AI</div>
          </div>
          {envelope.urgency && envelope.urgency !== "normal" && (
            <span className={`px-2 py-1 border-2 text-[10px] font-black uppercase tracking-widest ${urgencyStyle.bg}`}>
              {urgencyStyle.label}
            </span>
          )}
        </div>
      </header>

      {isEmergency && (
        <div className="bg-red-600 text-white text-center px-3 py-2 text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 flex-shrink-0"
          data-testid="triage-emergency-banner">
          <Warning weight="fill" size={14}/> Emergency — call the crew now if you can't wait
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 max-w-lg mx-auto w-full" data-testid="triage-messages">
        {messages.map((m, i) => (
          <MessageBubble key={i} msg={m}/>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="triage-thinking">
            <div className="bg-white border-2 border-violet-600 px-3 py-2 flex items-center gap-1.5">
              <Sparkle weight="fill" size={14} className="text-violet-600 animate-pulse"/>
              <div className="flex gap-1">
                <div className="h-1.5 w-1.5 bg-violet-600 rounded-full animate-bounce"/>
                <div className="h-1.5 w-1.5 bg-violet-600 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }}/>
                <div className="h-1.5 w-1.5 bg-violet-600 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }}/>
              </div>
            </div>
          </div>
        )}
        {envelope.likely_issue && !sending && (
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 text-center py-1" data-testid="triage-likely">
            Working theory: {envelope.likely_issue}
          </div>
        )}
      </div>

      <div className="bg-white border-t-2 border-[#0f172a] flex-shrink-0" data-testid="triage-composer">
        {/* Suggested quick replies from Claude */}
        {envelope.quick_replies && envelope.quick_replies.length > 0 && !sending && (
          <div className="p-2 border-b-2 border-slate-200 flex flex-wrap gap-1.5" data-testid="triage-quick-replies">
            {envelope.quick_replies.slice(0, 6).map((qr, i) => (
              <button key={i} onClick={() => sendMessage(qr)} data-testid={`triage-qr-${i}`}
                className="px-3 py-1.5 text-xs font-black border-2 border-violet-600 text-violet-600 hover:bg-violet-50">
                {qr}
              </button>
            ))}
          </div>
        )}

        {/* Photo request from Claude */}
        {envelope.photo_request && !sending && (
          <div className="px-3 py-2 bg-cyan-50 border-b-2 border-cyan-200 text-[11px] font-bold text-cyan-900 flex items-center gap-2" data-testid="triage-photo-req">
            <Camera size={14} weight="fill"/> Can you snap a photo of: {envelope.photo_request}?
          </div>
        )}

        {uploadingPhotos.length > 0 && (
          <div className="px-3 py-2 flex gap-2 overflow-x-auto border-b-2 border-slate-200" data-testid="triage-upload-strip">
            {uploadingPhotos.map((u, i) => (
              <div key={i} className={`h-14 w-14 flex-shrink-0 flex items-center justify-center text-[9px] font-black text-white ${u.pending ? "bg-slate-400 animate-pulse" : "bg-emerald-600"}`}>
                {u.pending ? "…" : "✓"}
              </div>
            ))}
          </div>
        )}

        <div className="p-2 flex items-center gap-2">
          <input ref={galleryRef} type="file" accept="image/*" multiple className="sr-only" capture="environment"
            onChange={e => { if (e.target.files?.length) { onPickPhotos(e.target.files); e.target.value = ""; }}}/>
          <button onClick={() => galleryRef.current?.click()} disabled={sending}
            data-testid="triage-photo-btn"
            className="h-11 w-11 border-2 border-slate-300 flex items-center justify-center hover:bg-slate-50 disabled:opacity-40">
            <Camera weight="fill" size={18}/>
          </button>
          <input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !sending && sendMessage()}
            placeholder="Type your reply…" disabled={sending}
            data-testid="triage-input"
            className="flex-1 h-11 px-3 border-2 border-slate-300 focus:border-[#0f172a] focus:outline-none text-sm"/>
          <button onClick={() => sendMessage()} disabled={sending || busy}
            data-testid="triage-send"
            className="h-11 w-11 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white flex items-center justify-center">
            <PaperPlaneRight weight="fill" size={16}/>
          </button>
        </div>

        <div className="p-2 border-t-2 border-slate-100 grid grid-cols-2 gap-2" data-testid="triage-outcomes">
          <button onClick={markResolved} disabled={busy}
            data-testid="triage-resolved-btn"
            className="h-11 border-2 border-emerald-600 bg-emerald-50 hover:bg-emerald-600 hover:text-white text-emerald-900 font-black uppercase text-[11px] tracking-widest transition-colors inline-flex items-center justify-center gap-1">
            <CheckCircle weight="fill" size={12}/> That Fixed It
          </button>
          <button onClick={escalate} disabled={busy || !canEscalate}
            data-testid="triage-escalate-btn"
            title={canEscalate ? "Send this to maintenance" : "Attach at least one photo to enable sending"}
            className={`h-11 font-black uppercase text-[11px] tracking-widest inline-flex items-center justify-center gap-1 transition-all ${
              !canEscalate
                ? "border-2 border-slate-300 bg-slate-100 text-slate-400 cursor-not-allowed"
                : (envelope.ready_for_wo || isEmergencyState
                    ? "bg-red-600 hover:bg-red-700 text-white animate-pulse-slow"
                    : "border-2 border-slate-300 text-slate-600 hover:bg-slate-50")
            }`}>
            <Wrench weight="fill" size={12}/> Send to Maintenance
          </button>
        </div>
        {!canEscalate && (
          <div className="px-3 pb-2 -mt-1 flex items-center justify-between gap-2 text-[11px]" data-testid="triage-photo-gate">
            <div className="flex items-center gap-1 text-slate-600 font-medium">
              <Camera weight="fill" size={11} className="text-red-500"/>
              <span>📸 Attach at least 1 photo to send to maintenance.</span>
            </div>
            <button onClick={() => { setAllowNoPhoto(true); toast.info("Send anyway enabled — tap Send to Maintenance again"); }}
              data-testid="triage-photo-override"
              className="text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-[#0f172a] underline">
              Can't take one → override
            </button>
          </div>
        )}
        {allowNoPhoto && totalPhotos === 0 && !isEmergencyState && (
          <div className="px-3 pb-2 -mt-1 text-[10px] font-black uppercase tracking-widest text-amber-700" data-testid="triage-override-active">
            ⚠ Override on — sending without a photo may slow the tech down
          </div>
        )}
      </div>
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`triage-msg-${msg.role}`}>
      <div className={`max-w-[85%] px-3 py-2 border-2 ${
        isUser
          ? "bg-[#0f172a] text-white border-[#0f172a]"
          : "bg-white text-slate-900 border-violet-600"
      }`}>
        {!isUser && (
          <div className="text-[9px] font-black uppercase tracking-widest text-violet-600 mb-0.5 inline-flex items-center gap-1">
            <Sparkle weight="fill" size={9}/> Field Radar AI
          </div>
        )}
        <div className="text-sm whitespace-pre-wrap">{msg.text}</div>
        {msg.photos && msg.photos.length > 0 && (
          <div className="mt-1.5 flex gap-1">
            {msg.photos.map((p, i) => (
              <div key={i} className="h-12 w-12 bg-slate-200 border border-white/40 flex items-center justify-center text-[9px] font-black text-slate-600">
                ✓
              </div>
            ))}
          </div>
        )}
        {!isUser && msg.illustration_key && <Illustration illustrationKey={msg.illustration_key}/>}
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
